import { Fragment } from 'react';
import { AccordionLink } from '../../components/AccordionLink';
import { Button } from '../../components/Button';
import { ContentParser } from '../../components/ContentParser';
import { Counter } from '../../components/Counter';
import { ExpandableText } from '../../components/ExpandableText';
import { DiscountPriceDisplay } from '../../components/PriceDisplay';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import { ShippingIcon } from '../../icons/ShippingIcon';
import { StoreIcon } from '../../icons/StoreIcon';
import {
  DescriptionSection,
  FieldLabel,
  FulfilmentItem,
  FulfilmentRow,
  FulfilmentSeparator,
  OptionsRow,
  PriceRow,
  PromotionBadge,
  Section,
  SpecificationsSection,
  Title,
  Wrapper,
} from './ProductContentPanel.styles';
import type { ProductContentPanelProps, ProductFulfilmentType } from './ProductContentPanel.types';

function fulfilmentIcon(type: ProductFulfilmentType) {
  return type === 'collection' ? <StoreIcon size={18} /> : <ShippingIcon size={18} />;
}

export function ProductContentPanel({
  title,
  price,
  discount,
  promotionName,
  options,
  selectedOptionId,
  onSelectOption,
  optionsLabel = 'Option',
  quantity = 1,
  onQuantityChange,
  minQuantity = 1,
  maxQuantity,
  quantityLabel = 'Quantity',
  onAddToBasket,
  addToBasketLabel = 'Add to basket',
  addToBasketDisabled,
  fulfilment,
  description,
  showMoreLabel,
  showLessLabel,
  specifications,
  specificationsLabel = 'Product specifications',
  isLoading = false,
}: ProductContentPanelProps) {
  if (isLoading) {
    return (
      <Wrapper>
        <SkeletonBlock variant="heading" />
        <PriceRow>
          <SkeletonBlock width="35%" height="1.5rem" />
        </PriceRow>
        <Section>
          <SkeletonBlock width="25%" height="0.875rem" />
        </Section>
        <Section>
          <SkeletonBlock variant="button" />
        </Section>
      </Wrapper>
    );
  }

  const hasOptions = options && options.length > 0;
  const hasFulfilment = fulfilment && fulfilment.length > 0;

  return (
    <Wrapper>
      {promotionName && <PromotionBadge>{promotionName}</PromotionBadge>}
      <Title>{title}</Title>

      {price && (
        <PriceRow>
          <DiscountPriceDisplay price={price} discount={discount} size="large" />
        </PriceRow>
      )}

      {hasOptions && (
        <Section>
          <FieldLabel>{optionsLabel}</FieldLabel>
          <OptionsRow>
            {options.map((option) => (
              <Button
                key={option.id}
                variant="toggle"
                selected={option.id === selectedOptionId}
                disabled={option.disabled}
                onClick={() => onSelectOption?.(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </OptionsRow>
        </Section>
      )}

      {onQuantityChange && (
        <Section>
          <FieldLabel>{quantityLabel}</FieldLabel>
          <Counter
            value={quantity}
            min={minQuantity}
            max={maxQuantity}
            onChange={onQuantityChange}
          />
        </Section>
      )}

      {onAddToBasket && (
        <StickyActionBar>
          <Button
            variant="primary"
            fullWidth
            onClick={onAddToBasket}
            disabled={addToBasketDisabled}
          >
            {addToBasketLabel}
          </Button>
        </StickyActionBar>
      )}

      {hasFulfilment && (
        <FulfilmentRow>
          {fulfilment.map((item, index) => (
            <Fragment key={`${item.type}-${index}`}>
              {index > 0 && <FulfilmentSeparator aria-hidden="true">•</FulfilmentSeparator>}
              <FulfilmentItem>
                {fulfilmentIcon(item.type)}
                {item.label}
              </FulfilmentItem>
            </Fragment>
          ))}
        </FulfilmentRow>
      )}

      {description && (
        <DescriptionSection>
          <ExpandableText
            text={description}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
          />
        </DescriptionSection>
      )}

      {specifications && (
        <SpecificationsSection>
          <AccordionLink label={specificationsLabel}>
            <ContentParser text={specifications} />
          </AccordionLink>
        </SpecificationsSection>
      )}
    </Wrapper>
  );
}
